/**
 * BookCard - Calil 検索結果の 1 冊分カード
 *
 * クリックで展開して所蔵館一覧をカード内に表示する。
 * 展開時に ISBN 二次検索を発火し、フリーワード検索では取得できなかった
 * 図書館 (例: 三郷市) の所蔵情報を補完する。
 * さらに OPAC 詳細ページから予約数と貸出可否を取得してチップに色で表現する。
 * 所蔵館は「貸出可 → 予約 0 (全て貸出中) → 巡回数少ない順 → 情報未取得 → 未対応」の順に並べる。
 */

import { BookOpen, ExternalLink, Library, ListPlus, Loader2, Star } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import type { CalilBook } from '@/api/types/calil'
import type { LibraryFavoriteInput, LibraryFavoriteStatus } from '@/api/types/library-favorite'
import { LibraryPickupBadge } from '@/components/shared/library-pickup-badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { buttonVariants } from '@/components/ui/button-variants'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'
import { useLibraryFilterStore } from '@/store/useLibraryFilterStore'
import { normalizeIsbn, useLibrarySearchStore } from '@/store/useLibrarySearchStore'
import { useLibraryStatsStore } from '@/store/useLibraryStatsStore'

import { buildAmazonUrl } from '../lib/amazon-url'
import { quickAddBookTodo } from '../lib/book-todo-actions'
import { FAVORITE_STATUS_META } from '../lib/favorite-status'
import { isSafeHttpUrl, type RankedEntry } from '../lib/holding-chip'
import { collectHoldingIds } from '../lib/holdings'
import { deriveStats, rankEntry } from '../lib/library-stats'
import { BookCover } from './BookCover'
import { BookTodoSheet } from './BookTodoSheet'
import { HoldingChip } from './HoldingChip'
import { ReserveLibrarySheet } from './ReserveLibrarySheet'

type Props = {
  book: CalilBook
  expanded: boolean
  onToggle: () => void
  /**
   * カードの仕様切替。
   * - `search`: 検索タブでの表示 (スターのみ)
   * - `favorite`: お気に入りタブでの表示 (状態遷移ボタンを常時表示)
   */
  mode?: 'search' | 'favorite'
}

export const BookCard = ({ book, expanded, onToggle, mode = 'search' }: Props) => {
  const navigate = useNavigate()
  const mapping = useLibrarySearchStore((s) => s.mapping)
  const enrichBookByIsbn = useLibrarySearchStore((s) => s.enrichBookByIsbn)
  const ensureStats = useLibraryStatsStore((s) => s.ensureStats)
  // 統計マップ全体を subscribe: URL 1 件更新で再レンダーされるがカード全体で 1 度だけ
  const statsByUrl = useLibraryStatsStore((s) => s.byUrl)
  const fetchingUrls = useLibraryStatsStore((s) => s.fetchingUrls)
  const normalizedIsbn = useMemo(() => normalizeIsbn(book.isbn), [book.isbn])
  const amazonUrl = useMemo(
    () => buildAmazonUrl(normalizedIsbn ?? book.isbn, book.title),
    [normalizedIsbn, book.isbn, book.title],
  )
  const favoriteEntry = useLibraryFavoritesStore((s) => (normalizedIsbn ? s.byIsbn[normalizedIsbn] : undefined))
  // 自炊本 (local ドメイン Product) が紐付いていれば「開く」ボタンを出す
  const openLocalEpisodeId = favoriteEntry?.product?.first_episode_id ?? null
  const isFavorite = favoriteEntry != null
  const favoriteStatus: LibraryFavoriteStatus = favoriteEntry?.status ?? 'wishlist'
  const reservedLibraryId = favoriteEntry?.reserved_library_id ?? null
  const toggleFavorite = useLibraryFavoritesStore((s) => s.toggle)
  const updateFavoriteStatus = useLibraryFavoritesStore((s) => s.updateStatus)
  // 状態変更 PATCH 中は 3 ボタン全 disabled にして連打・逆行を防ぐ。
  const [statusPending, setStatusPending] = useState(false)
  // wishlist 以外の本を ☆ で外すと履歴も消えるため確認ダイアログを挙げる。
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  // 予約中にするときに「どの図書館で予約したか」を選ぶシート。
  const [reserveSheetOpen, setReserveSheetOpen] = useState(false)
  // フォーマットが 2 つ以上あるときに、どれで TODO を作るかを選ぶシート。
  const [todoSheetOpen, setTodoSheetOpen] = useState(false)
  const [todoPending, setTodoPending] = useState(false)
  // フィルタ外の所蔵館もこのカードだけ一時的に表示する
  const [showOtherHoldings, setShowOtherHoldings] = useState(false)
  const enrichedBook = useLibrarySearchStore((s) => (normalizedIsbn ? s.enrichedByIsbn[normalizedIsbn] : undefined))
  const isEnriching = useLibrarySearchStore((s) => (normalizedIsbn ? !!s.enrichingIsbns[normalizedIsbn] : false))
  const enrichAttempted = useLibrarySearchStore((s) =>
    normalizedIsbn ? !!s.enrichAttemptedIsbns[normalizedIsbn] : false,
  )
  const selectedLibraryIds = useLibraryFilterStore((s) => s.selectedLibraryIds)
  // 全館検索中は館ごとの OPAC へ予約数を問い合わせない (館数が多く負荷が大きいため)
  const searchAllLibraries = useLibraryFilterStore((s) => s.searchAllLibraries)

  // 所蔵館の一覧が必要な場面 (展開表示 / 予約先の選択)。
  const needHoldings = expanded || reserveSheetOpen

  // 必要になったら ISBN 二次検索を発火 (キャッシュ済み・進行中なら no-op)。
  // フリーワード検索では返らない図書館 (フリーワード API 未対応館) の所蔵を補完する。
  useEffect(() => {
    if (!needHoldings) return
    if (!normalizedIsbn) return
    void enrichBookByIsbn(normalizedIsbn)
  }, [needHoldings, normalizedIsbn, enrichBookByIsbn])

  // enrichment 済みならそちらのデータを優先。所蔵館の集合は「元 ∪ enriched」で完全和集合を取る。
  const effectiveBook: CalilBook = useMemo(() => {
    if (!enrichedBook) return book
    return mergeEnrichment(book, enrichedBook)
  }, [book, enrichedBook])

  const filterIds = useMemo(
    () => (!searchAllLibraries && selectedLibraryIds.length > 0 ? new Set(selectedLibraryIds) : null),
    [searchAllLibraries, selectedLibraryIds],
  )

  // カード閉状態のバッジは常にフィルタ適用の件数を表示する (選択館に該当なしなら 0)。
  const totalCount = useMemo(() => {
    const ids = collectHoldingIds(effectiveBook).filter((id) => !filterIds || filterIds.has(id))
    return ids.length
  }, [effectiveBook, filterIds])

  // 展開時の館一覧は、選択館に所蔵があればその選択館のみ、なければ全館表示にフォールバック。
  const expandedFilterIds = useMemo(() => {
    if (!filterIds) return null
    for (const id of collectHoldingIds(effectiveBook)) {
      if (filterIds.has(id)) return filterIds
    }
    return null
  }, [filterIds, effectiveBook])

  // 館情報と統計を結合し、「早く借りられる順」にソートする。
  // 統計 subscribe 由来で再計算が走るため、フェッチ完了時に自動で並び順が更新される。
  // フィルタ外の所蔵館は otherEntries に分け、予約数は問い合わせず館名順で並べる。
  const { rankedEntries, otherEntries } = useMemo(() => {
    const ranked: RankedEntry[] = []
    const others: RankedEntry[] = []
    if (!needHoldings) return { rankedEntries: ranked, otherEntries: others }
    const urlMap = effectiveBook.url ?? {}
    for (const id of collectHoldingIds(effectiveBook)) {
      const url = urlMap[String(id)] ?? null
      const name = mapping[String(id)] ?? `library:${id}`
      if (expandedFilterIds && !expandedFilterIds.has(id)) {
        others.push({ libraryId: id, name, url, isFetching: false, derived: null })
        continue
      }
      const stats = url ? statsByUrl[url] : undefined
      const isFetching = url ? !!fetchingUrls[url] : false
      ranked.push({ libraryId: id, name, url, isFetching, derived: deriveStats(stats) })
    }

    ranked.sort((a, b) => {
      const ra = rankEntry(a.derived, a.isFetching)
      const rb = rankEntry(b.derived, b.isFetching)
      if (ra.bucket !== rb.bucket) return ra.bucket - rb.bucket
      if (ra.key !== rb.key) return ra.key - rb.key
      return a.name.localeCompare(b.name, 'ja')
    })
    others.sort((a, b) => a.name.localeCompare(b.name, 'ja'))
    return { rankedEntries: ranked, otherEntries: others }
  }, [needHoldings, effectiveBook, mapping, expandedFilterIds, statsByUrl, fetchingUrls])

  // 統計取得対象となる (library_id, url) を安定した参照でメモ化する。
  // effectiveBook.url が変わったときだけ再計算し、effect の再発火を最小化する。
  const statsItems = useMemo(() => {
    if (!needHoldings || searchAllLibraries) return []
    const urlMap = effectiveBook.url ?? {}
    const items: { library_id: number; url: string }[] = []
    for (const [idStr, url] of Object.entries(urlMap)) {
      if (!url || !isSafeHttpUrl(url)) continue
      const id = Number(idStr)
      if (!Number.isFinite(id)) continue
      if (expandedFilterIds && !expandedFilterIds.has(id)) continue
      items.push({ library_id: id, url })
    }
    return items
  }, [needHoldings, searchAllLibraries, effectiveBook, expandedFilterIds])

  // 展開されたら URL がある館の統計を裏で fetch (キャッシュ済み・進行中なら no-op)。
  // ISBN 二次検索が走る書誌は、その完了を待ってから 1 回で発火する
  // (enrichment 前後で URL 集合が変わるため。enrichment 未実施タイミングで発火すると
  //  2 回リクエストが飛び、相手 OPAC への負荷が増える)。ISBN が無い書誌は即発火する。
  const canFetchStats = !normalizedIsbn || enrichAttempted
  useEffect(() => {
    if (!canFetchStats) return
    if (statsItems.length === 0) return
    ensureStats(statsItems)
  }, [canFetchStats, statsItems, ensureStats])

  return (
    <>
      <Card
        onClick={onToggle}
        onKeyDown={(e) => {
          // 子ボタン (☆ / StatusControls / AlertDialogなど) の Enter/Space は親に伝搬させない。
          // 伝搬すると意図せずカードの展開状態が反転する。
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className={cn(
          'cursor-pointer gap-2 p-3 transition-colors hover:bg-accent/40 active:bg-accent/60',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          expanded && 'bg-accent/30',
        )}
      >
        {mode === 'favorite' && normalizedIsbn && isFavorite && (
          <div className="flex flex-col items-center gap-1.5">
            <StatusControls
              current={favoriteStatus}
              disabled={statusPending}
              onChange={(next) => {
                // 予約中にするときは「どの図書館で予約したか」を選んでから PATCH する。
                // 既に予約中の本は同じシートで予約先を選び直せる。
                if (next === 'reserved') {
                  setReserveSheetOpen(true)
                  return
                }
                const isbn = normalizedIsbn
                setStatusPending(true)
                void updateFavoriteStatus(isbn, next).finally(() => setStatusPending(false))
              }}
            />
            {favoriteStatus === 'reserved' && (
              // 館名は一覧のグループ見出しに出ているため、ここでは変更の導線だけ置く。
              <button
                type="button"
                disabled={statusPending}
                onClick={(e) => {
                  e.stopPropagation()
                  setReserveSheetOpen(true)
                }}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-xs text-emerald-300 transition-colors hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <Library className="size-3" />
                図書館を変更
              </button>
            )}
            {favoriteStatus === 'reserved' && favoriteEntry && <LibraryPickupBadge favorite={favoriteEntry} />}
          </div>
        )}
        <div className="flex items-start gap-3">
          {amazonUrl ? (
            // 右側のボタンを減らすため、Amazon は表紙のタップで開く
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                window.open(amazonUrl, '_blank', 'noopener,noreferrer')
              }}
              aria-label="Amazonで開く"
              title="Amazonで開く"
              className="shrink-0 rounded-sm p-0! transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <BookCover isbn={normalizedIsbn} className="w-16" />
            </button>
          ) : (
            <BookCover isbn={normalizedIsbn} className="w-16" />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="wrap-break-word text-sm font-medium leading-snug">{book.title}</div>
            {book.author && <div className="wrap-break-word text-xs text-muted-foreground">{book.author}</div>}
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {book.publisher && <span>{book.publisher}</span>}
              {book.pubdate && <span>{book.pubdate}</span>}
              {book.isbn && book.isbn !== '？' && <span>ISBN {book.isbn}</span>}
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1 text-emerald-400">
                <Library className="size-3.5" />
                所蔵 {totalCount}館
                {isEnriching && <Loader2 className="ml-1 size-3 animate-spin text-muted-foreground" />}
              </div>
              {amazonUrl && (
                // 全体の a の色は暗いカードで読みにくいので、文字色を指定する
                <a
                  href={amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex shrink-0 items-center gap-0.5 font-normal text-muted-foreground hover:text-foreground"
                >
                  amazon
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              )}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-center gap-2 text-muted-foreground">
            {normalizedIsbn && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  // wishlist 以外 (予約中 / 履歴) の本をスターで外すと履歴も消えるのでダイアログで確認を取る。
                  if (isFavorite && favoriteStatus !== 'wishlist') {
                    setConfirmRemoveOpen(true)
                    return
                  }
                  void toggleFavorite(buildFavoriteInput(book, normalizedIsbn))
                }}
                aria-label={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                aria-pressed={isFavorite}
                className="p-1 transition-colors hover:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Star
                  className={cn('size-5', isFavorite ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground')}
                />
              </button>
            )}
            <button
              type="button"
              disabled={todoPending}
              onClick={(e) => {
                e.stopPropagation()
                setTodoPending(true)
                const fields = {
                  title: book.title,
                  author: book.author,
                  publisher: book.publisher,
                  isbn: normalizedIsbn,
                }
                void quickAddBookTodo(fields, () => setTodoSheetOpen(true)).finally(() => setTodoPending(false))
              }}
              aria-label="タスクに追加"
              title="タスクに追加"
              className="p-1 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <ListPlus className="size-5" />
            </button>
            {openLocalEpisodeId && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(PATHS.P.EPISODE.replace(':episode_id', openLocalEpisodeId))
                }}
                aria-label="自炊本を開く"
                title="自炊本を開く"
                className="p-1 transition-colors hover:text-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <BookOpen className="size-5" />
              </button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="flex flex-wrap gap-1.5 border-t border-border/40">
            {rankedEntries.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                {isEnriching ? '所蔵館情報を取得中...' : '所蔵館情報がありません'}
              </div>
            ) : (
              rankedEntries.map((entry) => <HoldingChip key={entry.libraryId} entry={entry} />)
            )}
            {showOtherHoldings && otherEntries.map((entry) => <HoldingChip key={entry.libraryId} entry={entry} />)}
            {otherEntries.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowOtherHoldings((v) => !v)
                }}
                aria-expanded={showOtherHoldings}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {showOtherHoldings ? '選択館だけに戻す' : `＋ 他の ${otherEntries.length}館も表示`}
              </button>
            )}
          </div>
        )}

        {normalizedIsbn && (
          <AlertDialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>お気に入りから削除しますか？</AlertDialogTitle>
                <AlertDialogDescription>
                  {favoriteStatus === 'reserved'
                    ? '「予約中」の本です。削除すると履歴にも残りません。'
                    : '「履歴」の本です。削除すると履歴からも消えます。'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={(e) => e.stopPropagation()}>キャンセル</AlertDialogCancel>
                <AlertDialogAction
                  className={buttonVariants({ variant: 'destructive' })}
                  onClick={(e) => {
                    // React イベントは親 Cardの onClick までバブルするので、カード展開トリガーを防ぐ。
                    e.stopPropagation()
                    void toggleFavorite(buildFavoriteInput(book, normalizedIsbn))
                  }}
                >
                  削除
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </Card>

      <BookTodoSheet book={book} isbn={normalizedIsbn} open={todoSheetOpen} onOpenChange={setTodoSheetOpen} />

      {normalizedIsbn && (
        <ReserveLibrarySheet
          open={reserveSheetOpen}
          onOpenChange={setReserveSheetOpen}
          bookTitle={book.title}
          entries={rankedEntries}
          loading={isEnriching}
          reserved={favoriteStatus === 'reserved'}
          currentLibraryId={reservedLibraryId}
          currentLibraryName={favoriteEntry?.reserved_library_name ?? null}
          onSelect={(library) => {
            const isbn = normalizedIsbn
            setStatusPending(true)
            void updateFavoriteStatus(isbn, 'reserved', library).finally(() => setStatusPending(false))
          }}
        />
      )}
    </>
  )
}

/**
 * 元の book と ISBN 二次検索結果を和集合でマージする。
 * enrichment 側の url は OPAC リンクとして優先し、holdings/estimated は両方の和を取る。
 */
const mergeEnrichment = (original: CalilBook, enriched: CalilBook): CalilBook => {
  const holdings = Array.from(new Set([...(original.holdings ?? []), ...(enriched.holdings ?? [])]))
  const estimated = Array.from(
    new Set([...(original.estimated_holdings ?? []), ...(enriched.estimated_holdings ?? [])]),
  )
  const url = { ...(original.url ?? {}), ...(enriched.url ?? {}) }
  return {
    ...original,
    holdings,
    estimated_holdings: estimated,
    url,
  }
}

/**
 * `toggleFavorite` / `AlertDialogAction` で同じ形の input を組み立てていたのでヘルパーに抽出。
 * (削除だけなら isbn のみで良いが、`toggle` は未登録だと登録も行うためフルセットを渡す。)
 */
const buildFavoriteInput = (book: CalilBook, normalizedIsbn: string): LibraryFavoriteInput => ({
  isbn: normalizedIsbn,
  title: book.title,
  author: book.author ?? null,
  publisher: book.publisher ?? null,
  pubdate: book.pubdate != null ? String(book.pubdate) : null,
  cover: book.cover ?? null,
})

/**
 * 貸出ライフサイクル状態を切り替える 3 ボタン。
 * `favorite` モードのカードのみ表示する。カード上部に横並びで配置。
 * WAI-ARIA の radiogroup パターンにするには矢印キーナビが必要だが、未実装のためここでは
 * 「独立した 3 つのトグルボタン」として `aria-pressed` で表現する (不完全な radiogroup を避ける)。
 * 現在状態のボタンはハイライト。連打防止のため PATCH 中は全体を disabled にする。
 * 「予約中」だけは選択済みでも押せるようにし、予約した図書館を選び直す導線を兼ねる。
 * ラベル・アイコン・色は `lib/favorite-status.ts` の `FAVORITE_STATUS_META` を参照 (一元化)。
 */
const StatusControls = ({
  current,
  disabled,
  onChange,
}: {
  current: LibraryFavoriteStatus
  disabled: boolean
  onChange: (next: LibraryFavoriteStatus) => void
}) => {
  return (
    <div className="flex flex-row items-center justify-center gap-1">
      {FAVORITE_STATUS_META.map(({ status, label, icon: Icon, activeTextClass }) => {
        const isActive = current === status
        const reselectable = status === 'reserved'
        const actionLabel = isActive && reselectable ? '予約した図書館を変更' : `状態を「${label}」に変更`
        return (
          <button
            key={status}
            type="button"
            disabled={disabled}
            aria-pressed={isActive}
            onClick={(e) => {
              // 親 Card の onClick に伝搬しないようカード展開トリガーを防ぐ。
              e.stopPropagation()
              if (isActive && !reselectable) return
              onChange(status)
            }}
            aria-label={actionLabel}
            title={isActive && reselectable ? actionLabel : label}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? cn('border-current bg-accent/40', !reselectable && 'cursor-default', activeTextClass)
                : 'border-border/60 bg-muted/20 text-muted-foreground hover:bg-muted/40 hover:text-foreground',
              disabled && !isActive && 'cursor-not-allowed opacity-50 hover:bg-muted/20 hover:text-muted-foreground',
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}
